import React, {useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {batch, useDispatch, useSelector} from 'react-redux';
import {TaskType, PipettingParameterFieldKey} from 'constants/dataEnum';
import jsonCopy from 'lib/jsonCopy';
import {restoreCardTaskState} from 'stores/actions/cardStateAction';
import {updateRunningProtocol} from 'stores/business/protocolManagementBusiness';
import {globalAddUndoItem} from 'stores/business/undoRedoBusinessAction';
import PipettingProfileItems from 'contents/ProtocolEditor/TaskList/PipettingProfile/PipettingProfileItem/PipettingProfileItems';
import PipettingProfileWrapper from 'contents/ProtocolEditor/TaskList/PipettingProfile/PipettingProfileWrapper';
import 'contents/ProtocolEditor/TaskList/PipettingProfile/PipettingProfile.scss';
import PipettingProfileExpanderButton from 'contents/ProtocolEditor/TaskList/PipettingProfile/PipettingProfileExpanderButton';
import {getTasks as getTasksfromCardState} from 'stores/selectors/cardStateSelector';
import {calculateTipPositionOffset} from 'lib/PipettingProfileItemHelper';
import {getLabwareState} from 'stores/selectors/labwareStateSelectors';
import {find} from 'lib/objectHelper';

function NormalizationPipettingProfileContainer({runtimeMode, selectedCard}) {
  const dispatch = useDispatch();
  const cardStateTasks = useSelector(getTasksfromCardState);
  const labwareState = useSelector(getLabwareState);
  const cardStateTasksRef = useRef(null);
  const [expand, setExpand] = useState(false);

  useEffect(() => {
    cardStateTasksRef.current = cardStateTasks;
  }, [cardStateTasks]);

  const aspirateTask = useMemo(
    () =>
      Object.values(cardStateTasks).find(
        (task) => selectedCard?.tasks?.includes(task.ID) && task.type === TaskType.ASPIRATE,
      ) || {},
    [cardStateTasks, selectedCard?.tasks],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dispenseTask = useMemo(
    () =>
      Object.values(cardStateTasks).find(
        (task) => selectedCard?.tasks?.includes(task.ID) && task.type === TaskType.DISPENSE,
      ) || {},
  );

  const getTasks = (taskType) => {
    let tasks = [];

    if (!taskType) {
      tasks = Object.values(cardStateTasksRef.current).filter((t) => selectedCard?.tasks?.includes(t.ID));
    } else {
      tasks = Object.values(cardStateTasksRef.current).filter(
        (t) => selectedCard?.tasks?.includes(t.ID) && t.type === taskType,
      );
    }

    return tasks;
  };

  function restoreTasksByPipettingParamater(val, taskType, pipettingParam) {
    const newTasks = jsonCopy(cardStateTasksRef.current);
    let newVal = val;
    if (
      pipettingParam === PipettingParameterFieldKey.MIXING_POSITION ||
      pipettingParam === PipettingParameterFieldKey.TIP_POSITION ||
      pipettingParam === PipettingParameterFieldKey.TIP_POSITION_X_OFFSET ||
      pipettingParam === PipettingParameterFieldKey.TIP_POSITION_Y_OFFSET
    ) {
      newVal = Number(val);
    }

    let direction = '';
    let positionOffset = {};
    const tasks = getTasks(taskType);

    if (
      pipettingParam === PipettingParameterFieldKey.TIP_POSITION_X_OFFSET ||
      pipettingParam === PipettingParameterFieldKey.TIP_POSITION_Y_OFFSET
    ) {
      const {labwareID, tipXPositionOffset, tipYPositionOffset} = tasks[0] || {};
      const labware = find(labwareState, {LabwareID: labwareID});
      direction = pipettingParam === PipettingParameterFieldKey.TIP_POSITION_X_OFFSET ? 'x' : 'y';
      positionOffset = calculateTipPositionOffset(direction, newVal, tipYPositionOffset, tipXPositionOffset, labware);
    }

    tasks.forEach((t) => {
      if (pipettingParam === PipettingParameterFieldKey.MIXING_POSITION_ASPIRATE_AND_DISPENSE) {
        newTasks[t.ID][PipettingParameterFieldKey.MIXING_POSITION_ASPIRATE] = newVal;
        newTasks[t.ID][PipettingParameterFieldKey.MIXING_POSITION_DISPENSE] = newVal;
      } else if (
        pipettingParam === PipettingParameterFieldKey.TIP_POSITION_X_OFFSET ||
        pipettingParam === PipettingParameterFieldKey.TIP_POSITION_Y_OFFSET
      ) {
        newTasks[t.ID][PipettingParameterFieldKey.TIP_POSITION_X_OFFSET] = positionOffset.xOffset;
        newTasks[t.ID][PipettingParameterFieldKey.TIP_POSITION_Y_OFFSET] = positionOffset.yOffset;
      } else {
        newTasks[t.ID][pipettingParam] = newVal;
      }
    });
    dispatch(restoreCardTaskState(newTasks));
  }

  const updatePipettingProfileValue = (key, value, taskType) => {
    batch(() => {
      restoreTasksByPipettingParamater(value, taskType, key);

      if (!runtimeMode) {
        dispatch(globalAddUndoItem());
        return;
      }

      dispatch(updateRunningProtocol());
    });
  };

  return (
    <div key={selectedCard?.ID} className="pipetting-profile">
      <div className="pipetting-profile-content">
        {expand && (
          <PipettingProfileWrapper>
            <PipettingProfileItems.TipPosition
              task={aspirateTask}
              onChange={(value) => {
                updatePipettingProfileValue(PipettingParameterFieldKey.TIP_POSITION, value, TaskType.ASPIRATE);
              }}
              onXTipPositionOnChange={(value) => {
                updatePipettingProfileValue(PipettingParameterFieldKey.TIP_POSITION_X_OFFSET, value, TaskType.ASPIRATE);
              }}
              onYTipPositionOnChange={(value) => {
                updatePipettingProfileValue(PipettingParameterFieldKey.TIP_POSITION_Y_OFFSET, value, TaskType.ASPIRATE);
              }}
            />
            <PipettingProfileItems.Speed
              task={aspirateTask}
              onChange={(value) => {
                updatePipettingProfileValue(PipettingParameterFieldKey.SPEED, value, TaskType.ASPIRATE);
              }}
            />
            <PipettingProfileItems.RetractSpeed
              task={aspirateTask}
              onChange={(value) => {
                updatePipettingProfileValue(PipettingParameterFieldKey.RETRACT_SPEED, value, TaskType.ASPIRATE);
              }}
            />
            <PipettingProfileItems.TipTouchOff
              task={aspirateTask}
              onChangeSwitch={(value) => {
                updatePipettingProfileValue(PipettingParameterFieldKey.TIP_TOUCH_OFF, value, TaskType.ASPIRATE);
              }}
              onChangeDelay={(value) => {
                updatePipettingProfileValue(PipettingParameterFieldKey.TIP_TOUCH_OFF_DELAY, value, TaskType.ASPIRATE);
              }}
              onChangeHeight={(value) =>
                updatePipettingProfileValue(PipettingParameterFieldKey.TIP_TOUCH_OFF_HEIGHT, value, TaskType.ASPIRATE)
              }
              onChangeDirectionX={(value) =>
                updatePipettingProfileValue(
                  PipettingParameterFieldKey.TIP_TOUCH_OFF_DIRECTION_X,
                  value,
                  TaskType.ASPIRATE,
                )
              }
              onChangeDirectionY={(value) =>
                updatePipettingProfileValue(
                  PipettingParameterFieldKey.TIP_TOUCH_OFF_DIRECTION_Y,
                  value,
                  TaskType.ASPIRATE,
                )
              }
            />
            <PipettingProfileItems.TipPosition
              task={dispenseTask}
              onChange={(value) => {
                updatePipettingProfileValue(PipettingParameterFieldKey.TIP_POSITION, value, TaskType.DISPENSE);
              }}
              onXTipPositionOnChange={(value) => {
                updatePipettingProfileValue(PipettingParameterFieldKey.TIP_POSITION_X_OFFSET, value, TaskType.DISPENSE);
              }}
              onYTipPositionOnChange={(value) => {
                updatePipettingProfileValue(PipettingParameterFieldKey.TIP_POSITION_Y_OFFSET, value, TaskType.DISPENSE);
              }}
            />
            <PipettingProfileItems.PrecisionDispense
              task={dispenseTask}
              onChange={(value) => {
                updatePipettingProfileValue(PipettingParameterFieldKey.PRECISION_DISPENSE, value, TaskType.DISPENSE);
              }}
            />
            <PipettingProfileItems.TipTouchOff
              task={dispenseTask}
              onChangeSwitch={(value) => {
                updatePipettingProfileValue(PipettingParameterFieldKey.TIP_TOUCH_OFF, value, TaskType.DISPENSE);
              }}
              onChangeDelay={(value) => {
                updatePipettingProfileValue(PipettingParameterFieldKey.TIP_TOUCH_OFF_DELAY, value, TaskType.DISPENSE);
              }}
              onChangeHeight={(value) =>
                updatePipettingProfileValue(PipettingParameterFieldKey.TIP_TOUCH_OFF_HEIGHT, value, TaskType.DISPENSE)
              }
              onChangeDirectionX={(value) =>
                updatePipettingProfileValue(
                  PipettingParameterFieldKey.TIP_TOUCH_OFF_DIRECTION_X,
                  value,
                  TaskType.DISPENSE,
                )
              }
              onChangeDirectionY={(value) =>
                updatePipettingProfileValue(
                  PipettingParameterFieldKey.TIP_TOUCH_OFF_DIRECTION_Y,
                  value,
                  TaskType.DISPENSE,
                )
              }
            />
            <PipettingProfileItems.Speed
              task={dispenseTask}
              onChange={(value) => {
                updatePipettingProfileValue(PipettingParameterFieldKey.SPEED, value, TaskType.DISPENSE);
              }}
            />
            <PipettingProfileItems.RetractSpeed
              task={dispenseTask}
              onChange={(value) => {
                updatePipettingProfileValue(PipettingParameterFieldKey.RETRACT_SPEED, value, TaskType.DISPENSE);
              }}
            />
            <PipettingProfileItems.EnableMixing
              task={aspirateTask}
              onChange={(value) => {
                updatePipettingProfileValue(PipettingParameterFieldKey.ENABLE_MIXING, value, TaskType.ASPIRATE);
              }}
            />
            <PipettingProfileItems.MixingDetailGroup
              task={aspirateTask}
              onChangeMixingPosition={(value) => {
                updatePipettingProfileValue(
                  PipettingParameterFieldKey.MIXING_POSITION_ASPIRATE_AND_DISPENSE,
                  value,
                  TaskType.ASPIRATE,
                );
              }}
              onChangeMixingPositionAspirate={(value) => {
                updatePipettingProfileValue(
                  PipettingParameterFieldKey.MIXING_POSITION_ASPIRATE,
                  value,
                  TaskType.ASPIRATE,
                );
              }}
              onChangeMixingPositionDispense={(value) => {
                updatePipettingProfileValue(
                  PipettingParameterFieldKey.MIXING_POSITION_DISPENSE,
                  value,
                  TaskType.ASPIRATE,
                );
              }}
              onChangeVolume={(value) => {
                updatePipettingProfileValue(PipettingParameterFieldKey.MIXING_VOLUME, value, TaskType.ASPIRATE);
              }}
              onChangeCycle={(value) => {
                updatePipettingProfileValue(PipettingParameterFieldKey.MIXING_CYCLE, value, TaskType.ASPIRATE);
              }}
              onChangeMixingSpeed={(value) => {
                updatePipettingProfileValue(PipettingParameterFieldKey.MIXING_SPEED, value, TaskType.ASPIRATE);
              }}
            />
            <PipettingProfileItems.EnableMixing
              task={dispenseTask}
              onChange={(value) => {
                updatePipettingProfileValue(PipettingParameterFieldKey.ENABLE_MIXING, value, TaskType.DISPENSE);
              }}
            />
            <PipettingProfileItems.MixingDetailGroup
              task={dispenseTask}
              onChangeMixingPosition={(value) => {
                updatePipettingProfileValue(
                  PipettingParameterFieldKey.MIXING_POSITION_ASPIRATE_AND_DISPENSE,
                  value,
                  TaskType.DISPENSE,
                );
              }}
              onChangeMixingPositionAspirate={(value) => {
                updatePipettingProfileValue(
                  PipettingParameterFieldKey.MIXING_POSITION_ASPIRATE,
                  value,
                  TaskType.DISPENSE,
                );
              }}
              onChangeMixingPositionDispense={(value) => {
                updatePipettingProfileValue(
                  PipettingParameterFieldKey.MIXING_POSITION_DISPENSE,
                  value,
                  TaskType.DISPENSE,
                );
              }}
              onChangeVolume={(value) => {
                updatePipettingProfileValue(PipettingParameterFieldKey.MIXING_VOLUME, value, TaskType.DISPENSE);
              }}
              onChangeCycle={(value) => {
                updatePipettingProfileValue(PipettingParameterFieldKey.MIXING_CYCLE, value, TaskType.DISPENSE);
              }}
              onChangeMixingSpeed={(value) => {
                updatePipettingProfileValue(PipettingParameterFieldKey.MIXING_SPEED, value, TaskType.DISPENSE);
              }}
            />
          </PipettingProfileWrapper>
        )}
      </div>
      <PipettingProfileExpanderButton isExpanded={expand} onClick={setExpand} />
    </div>
  );
}

NormalizationPipettingProfileContainer.propTypes = {
  runtimeMode: PropTypes.bool,
  selectedCard: PropTypes.object.isRequired,
};

NormalizationPipettingProfileContainer.defaultProps = {
  runtimeMode: false,
};

export default NormalizationPipettingProfileContainer;
